import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { ConfirmDialog } from './ConfirmDialog';

const monoFont = "'IBM Plex Mono', monospace";
const PAGE_SIZE = 30;

interface Snapshot {
  id: string;
  snapshot_number: number;
  description: string;
  triggered_by: string;
  snapshot_data: any;
  created_at: string;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const hours = `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;

  if (diff < 86400000 && d.getDate() === now.getDate()) return `hoje, ${hours}`;
  
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.getDate() === yesterday.getDate() && d.getMonth() === yesterday.getMonth()) return `ontem, ${hours}`;

  return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}, ${hours}`;
}

export function PublicationHistory() {
  const { session } = useAuth();
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);
  const [restoreTarget, setRestoreTarget] = useState<Snapshot | null>(null);
  const [restoring, setRestoring] = useState(false);

  const loadSnapshots = async (pageNum: number, append = false) => {
    const from = pageNum * PAGE_SIZE;
    const to = from + PAGE_SIZE;
    const { data } = await supabase
      .from('publication_snapshots')
      .select('*')
      .order('created_at', { ascending: false })
      .range(from, to);

    if (data) {
      const items = (data as unknown as Snapshot[]).slice(0, PAGE_SIZE);
      setHasMore(data.length > PAGE_SIZE);
      setSnapshots(prev => append ? [...prev, ...items] : items);
    }
    setLoading(false);
  };

  useEffect(() => { loadSnapshots(0); }, []);

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    loadSnapshots(next, true);
  };

  const handleRestore = async () => {
    if (!restoreTarget) return;
    setRestoring(true);

    const snap = restoreTarget.snapshot_data;

    // Restore home_images
    if (snap.home_images) {
      await supabase.from('home_images').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      if (snap.home_images.length > 0) {
        await supabase.from('home_images').insert(snap.home_images);
      }
    }

    // Restore projects
    if (snap.projects) {
      await supabase.from('project_images').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('projects').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      if (snap.projects.length > 0) {
        await supabase.from('projects').insert(snap.projects);
      }
      if (snap.project_images && snap.project_images.length > 0) {
        await supabase.from('project_images').insert(snap.project_images);
      }
    }

    // Restore menu_items
    if (snap.menu_items) {
      await supabase.from('menu_items').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      if (snap.menu_items.length > 0) {
        await supabase.from('menu_items').insert(snap.menu_items);
      }
    }

    // Restore site_settings (update only)
    if (snap.site_settings) {
      const { id, ...rest } = snap.site_settings;
      await supabase.from('site_settings').update(rest).eq('id', id);
    }

    // Create restore entry
    const userId = (await supabase.auth.getUser()).data.user?.id;
    await supabase.from('publication_snapshots').insert({
      user_id: userId,
      description: `Restaurado para snapshot #${restoreTarget.snapshot_number}`,
      triggered_by: 'restore',
      snapshot_data: snap,
    } as any);

    setRestoring(false);
    setRestoreTarget(null);
    setPage(0);
    loadSnapshots(0);
    window.dispatchEvent(new CustomEvent('cms-projects-updated'));
  };

  if (loading) {
    return <p style={{ fontFamily: monoFont, fontSize: 13, color: 'rgba(247,246,242,0.4)' }}>Carregando histórico...</p>;
  }

  return (
    <div>
      <h3
        style={{
          fontFamily: monoFont,
          fontSize: 12,
          fontWeight: 300,
          textTransform: 'uppercase',
          opacity: 0.4,
          letterSpacing: '0.1em',
          marginBottom: 16,
        }}
      >
        HISTÓRICO DE PUBLICAÇÕES
      </h3>

      {snapshots.length === 0 ? (
        <p style={{ fontFamily: monoFont, fontSize: 13, color: 'rgba(247,246,242,0.3)' }}>
          Nenhum snapshot registrado ainda.
        </p>
      ) : (
        <div>
          {snapshots.map((snap, i) => (
            <div
              key={snap.id}
              style={{
                borderBottom: i < snapshots.length - 1 ? '1px solid rgba(247,246,242,0.06)' : undefined,
                padding: '14px 0',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
              }}
            >
              {/* Dot */}
              <div
                style={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(247,246,242,0.3)',
                  marginTop: 7,
                  flexShrink: 0,
                }}
              />

              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                  <span style={{ fontFamily: monoFont, fontSize: 13, fontWeight: 300, color: '#F7F6F2' }}>
                    {snap.description}
                  </span>
                  <span style={{ fontFamily: monoFont, fontSize: 12, color: 'rgba(247,246,242,0.4)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {formatDate(snap.created_at)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                  <span style={{ fontFamily: monoFont, fontSize: 12, color: 'rgba(247,246,242,0.25)' }}>
                    Snapshot #{snap.snapshot_number}
                  </span>
                  <button
                    onClick={() => setRestoreTarget(snap)}
                    style={{
                      fontFamily: monoFont,
                      fontSize: 12,
                      fontWeight: 300,
                      padding: '4px 10px',
                      border: '0.5px solid rgba(247,246,242,0.2)',
                      backgroundColor: 'transparent',
                      color: 'rgba(247,246,242,0.5)',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.borderColor = 'rgba(247,246,242,0.8)';
                      e.currentTarget.style.color = 'rgba(247,246,242,0.8)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.borderColor = 'rgba(247,246,242,0.2)';
                      e.currentTarget.style.color = 'rgba(247,246,242,0.5)';
                    }}
                  >
                    RESTAURAR
                  </button>
                </div>
              </div>
            </div>
          ))}

          {hasMore && (
            <button
              onClick={handleLoadMore}
              style={{
                fontFamily: monoFont,
                fontSize: 12,
                fontWeight: 300,
                textTransform: 'uppercase',
                padding: '10px 20px',
                border: '0.5px solid rgba(247,246,242,0.15)',
                backgroundColor: 'transparent',
                color: 'rgba(247,246,242,0.5)',
                cursor: 'pointer',
                width: '100%',
                marginTop: 12,
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(247,246,242,0.4)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(247,246,242,0.15)'; }}
            >
              Carregar Mais
            </button>
          )}
        </div>
      )}

      {/* Restore confirmation */}
      {restoreTarget && (
        <ConfirmDialog
          open={!!restoreTarget}
          onOpenChange={(open) => { if (!open) setRestoreTarget(null); }}
          title={`Restaurar snapshot #${restoreTarget.snapshot_number}?`}
          description={`"${restoreTarget.description} — ${formatDate(restoreTarget.created_at)}"\n\nO estado atual do site será substituído por este ponto.\nEsta ação não pode ser desfeita.`}
          confirmLabel={restoring ? 'Restaurando...' : 'Restaurar'}
          cancelLabel="Cancelar"
          onConfirm={handleRestore}
          destructive
        />
      )}
    </div>
  );
}

// Helper to create a snapshot (called from publish actions)
export async function createPublicationSnapshot(description: string, triggeredBy: string) {
  const userId = (await supabase.auth.getUser()).data.user?.id;
  
  // Gather current state (RLS will auto-filter to user's data)
  const [
    { data: homeImages },
    { data: projects },
    { data: projectImages },
    { data: menuItems },
    { data: siteSettings },
  ] = await Promise.all([
    supabase.from('home_images').select('*').order('sort_order'),
    supabase.from('projects').select('*').order('sort_order'),
    supabase.from('project_images').select('*').order('sort_order'),
    supabase.from('menu_items').select('*').order('sort_order'),
    supabase.from('site_settings').select('*').limit(1).maybeSingle(),
  ]);

  const snapshotData = {
    home_images: homeImages || [],
    projects: projects || [],
    project_images: projectImages || [],
    menu_items: menuItems || [],
    site_settings: siteSettings || null,
  };

  await supabase.from('publication_snapshots').insert({
    user_id: userId,
    description,
    triggered_by: triggeredBy,
    snapshot_data: snapshotData,
  } as any);
}
